if (!customElements.get('scroll-header')) {
  class Header extends HTMLElement {
    constructor() {
      super();
      this.lastScrollY = window.scrollY;
      this.ticking = false;
      this.header = this.closest('header');

      this.mobileNav = this.querySelector('[data-mobile-nav]');
      this.mobileNavToggle = this.querySelector('[data-mobile-nav-toggle]');
      this.isNavOpen = false;

      this.menuItems = this.querySelectorAll('[data-menu-parent]');
      this.desktopMenuItems = this.querySelectorAll('[data-menu-item]');
      this.hoverTimeout = null;
      this.submenuTimeout = null;
      this.showTimeout = null;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.boundToggleMobileNav = this.toggleMobileNav.bind(this);
    }

    connectedCallback() {
      // Scroll behavior
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      document.addEventListener('keydown', this.onKeyDown);

      // Mobile nav toggle
      this.mobileNav.style.top = `${this.header.offsetHeight}px`;
      this.mobileNav.style.height = `calc(100dvh - ${this.header.offsetHeight}px)`;
      this.mobileNavToggle.addEventListener('click', this.boundToggleMobileNav);

      // Parent menu item click handler
      this.menuItems.forEach((item) => {
        item.addEventListener('click', (e) => this.toggleSubMenu(e));
      });

      // Back button click handler inside child menus
      this.querySelectorAll('[data-menu-child-header]').forEach((header) => {
        header.addEventListener('click', (e) => this.closeSubMenu(e));
      });

      // Set initial ARIA attributes
      this.mobileNavToggle.setAttribute('aria-expanded', 'false');
      this.mobileNav.setAttribute('aria-hidden', 'true');

      // Desktop navigation hover
      this.setupDesktopNavHover();
      this.setupChildSubmenuHover();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeyDown);
      this.mobileNavToggle.removeEventListener('click', this.boundToggleMobileNav);

      this.menuItems.forEach((item) => {
        item.removeEventListener('click', (e) => this.toggleSubMenu(e));
      });
      this.querySelectorAll('[data-menu-child-header]').forEach((header) => {
        header.removeEventListener('click', (e) => this.closeSubMenu(e));
      });

      // Clean up desktop nav hover events
      this.desktopMenuItems.forEach((menuItem) => {
        const parentLi = menuItem.closest('li');
        const dropdown = parentLi?.querySelector('[data-menu-child]');

        if (dropdown) {
          [menuItem, dropdown].forEach((element) => {
            element.removeEventListener('mouseenter', () => this.showDesktopDropdown(dropdown));
            element.removeEventListener('mouseleave', () => this.hideDesktopDropdown(dropdown));
          });
        }
      });

      // Clear any pending timeouts
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
      }

      if (this.submenuTimeout) {
        clearTimeout(this.submenuTimeout);
      }

      if (this.showTimeout) {
        clearTimeout(this.showTimeout);
      }
    }

    onResize() {
      this.mobileNav.style.top = `${this.header.offsetHeight}px`;
      this.mobileNav.style.height = `calc(100dvh - ${this.header.offsetHeight}px)`;

      if (window.innerWidth > 768 && this.isNavOpen) {
        this.toggleMobileNav();
      }
    }

    onKeyDown(e) {
      if (e.key !== 'Escape') return;

      if (this.isNavOpen) {
        this.toggleMobileNav();
        return;
      }

      const openMega = Array.from(this.querySelectorAll('[data-mega-menu]')).find((d) => !d.classList.contains('hidden'));
      if (openMega && openMega.contains(document.activeElement)) {
        e.preventDefault();
        this.hideAllDesktopDropdowns();
        openMega.closest('li')?.querySelector('[data-menu-item] a')?.focus();
      }
    }

    // Scroll logic
    onScroll() {
      if (this.ticking) return;

      window.requestAnimationFrame(() => {
        const headerH = this.header.offsetHeight;
        const currentY = window.scrollY;
        const cartOrderSummary = document.querySelector('[data-cart-order-summary]');

        if (currentY > this.lastScrollY) {
          // Scrolling down - hide header if past threshold
          if (currentY >= headerH) {
            this.header.classList.add('site-header__container--hidden');

            if (cartOrderSummary) {
              cartOrderSummary.classList.remove('header-showing');
            }
          }
        } else {
          if (!this.hasAttribute('data-disable-sticky-header')) {
            // Scrolling up - always show header
            this.header.classList.remove('site-header__container--hidden');

            if (currentY >= headerH) {
              this.header.classList.add('site-header__container--fixed');
            }
          }

          if (cartOrderSummary) {
            cartOrderSummary.classList.add('header-showing');
          }
        }

        if (currentY === 0) {
          // At top - remove all scroll classes
          this.header.classList.remove('site-header__container--fixed', 'site-header__container--hidden');

          if (cartOrderSummary) {
            cartOrderSummary.classList.remove('header-showing');
          }
        } else if (currentY >= headerH && !this.header.classList.contains('site-header__container--hidden')) {
          // Past header height and not hidden - should be fixed
          this.header.classList.add('site-header__container--fixed');
        }

        this.lastScrollY = currentY;
        this.ticking = false;
      });
      this.ticking = true;
    }

    // Main mobile nav toggle
    toggleMobileNav() {
      const wasOpen = this.isNavOpen;

      // 🧠 Ensure layout has settled before calculating dimensions
      window.requestAnimationFrame(() => {
        const headerHeight = this.header.offsetHeight;

        // ✅ Recalculate top & height based on latest header height
        this.mobileNav.style.top = `${headerHeight}px`;
        this.mobileNav.style.height = `calc(100dvh - ${headerHeight}px)`;

        // Toggle nav open/closed state
        this.mobileNav.classList.toggle('translate-x-0');
        this.isNavOpen = this.mobileNav.classList.contains('translate-x-0');

        if (!wasOpen && this.isNavOpen) {
          const currentScroll = window.scrollY;

          // Smooth-scroll to top if partially scrolled down
          if (currentScroll > 0 && currentScroll <= headerHeight) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }

          document.body.classList.add('overflow-hidden');
          this.mobileNavToggle.setAttribute('aria-expanded', 'true');
          this.mobileNav.setAttribute('aria-hidden', 'false');
        } else if (wasOpen && !this.isNavOpen) {
          document.body.classList.remove('overflow-hidden');
          this.mobileNavToggle.setAttribute('aria-expanded', 'false');
          this.mobileNav.setAttribute('aria-hidden', 'true');

          // Close any open submenus
          this.querySelectorAll('[data-menu-child].translate-x-0').forEach((childMenu) => {
            childMenu.classList.remove('translate-x-0');
          });
        }

        this.updateMobileNavIcon();
      });
    }

    updateMobileNavIcon() {
      const iconContainer = this.mobileNavToggle.querySelector('[data-icon]');
      const hamburgerIcon = iconContainer.querySelector('.hamburger');
      const closeIcon = iconContainer.querySelector('.close');

      if (this.isNavOpen) {
        hamburgerIcon.classList.add('hidden');
        closeIcon.classList.remove('hidden');
      } else {
        hamburgerIcon.classList.remove('hidden');
        closeIcon.classList.add('hidden');
      }
    }

    toggleSubMenu(e) {
      e.preventDefault();
      const parentItem = e.currentTarget.parentElement;
      const childMenu = parentItem.querySelector('[data-menu-child]');

      if (childMenu) {
        childMenu.classList.add('translate-x-0');
        document.body.classList.add('overflow-hidden');
      }
    }

    closeSubMenu(e) {
      e.preventDefault();
      e.stopPropagation();

      const header = e.currentTarget;
      const childMenu = header.closest('[data-menu-child]');

      if (childMenu) {
        childMenu.classList.remove('translate-x-0');
      }
    }

    // Desktop Nav — pointer hover unchanged; Tab stays on top-level links; Enter/Space open mega; arrows navigate inside.
    setupDesktopNavHover() {
      this.desktopMenuItems.forEach((menuItem) => {
        const parentLi = menuItem.closest('li');
        const dropdown = parentLi?.querySelector('[data-mega-menu]');

        if (!dropdown || !parentLi) return;

        const show = () => this.showDesktopDropdown(dropdown, false);
        const hide = () => this.hideDesktopDropdown(dropdown);

        [menuItem, dropdown].forEach((element) => {
          element.addEventListener('mouseenter', show);
          element.addEventListener('mouseleave', hide);
        });

        const topLink = menuItem.querySelector('a[href]');
        if (topLink) {
          topLink.addEventListener('keydown', (e) => this.handleDesktopTopLinkKeydown(e, dropdown, topLink));
        }

        parentLi.addEventListener('focusout', (e) => {
          const next = e.relatedTarget;
          if (next && parentLi.contains(next)) return;
          requestAnimationFrame(() => {
            if (parentLi.contains(document.activeElement)) return;
            hide();
          });
        });

        dropdown.addEventListener('keydown', (e) => this.handleMegaMenuKeydown(e, dropdown));
      });
    }

    handleDesktopTopLinkKeydown(e, dropdown, topLink) {
      if (e.repeat) return;

      const megaOpen = dropdown.classList.contains('flex') && !dropdown.classList.contains('hidden');

      if (e.key === 'Enter' || e.key === ' ') {
        if (!megaOpen) {
          e.preventDefault();
          this.showDesktopDropdown(dropdown, true);
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!megaOpen) {
          this.showDesktopDropdown(dropdown, true);
        }
        requestAnimationFrame(() => this.getMegaMenuFirstFocusable(dropdown)?.focus());
        return;
      }

      if (e.key === 'ArrowUp' && megaOpen) {
        e.preventDefault();
        this.hideAllDesktopDropdowns();
        topLink.focus();
      }
    }

    handleMegaMenuKeydown(e, megaMenu) {
      if (!megaMenu.classList.contains('flex') || megaMenu.classList.contains('hidden')) return;

      const navKeys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!navKeys.includes(e.key)) return;

      const active = document.activeElement;
      if (!megaMenu.contains(active)) return;

      const list = this.getMegaMenuFocusableList(megaMenu);
      if (list.length === 0) return;

      const idx = list.indexOf(/** @type {HTMLElement} */ (active));
      if (idx === -1) return;

      if (e.key === 'Home') {
        e.preventDefault();
        list[0]?.focus();
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        list[list.length - 1]?.focus();
        return;
      }

      const columnBtn = active.closest('button[data-child-handle]');

      if (e.key === 'ArrowRight' && columnBtn) {
        e.preventDefault();
        this.setActiveSubmenuByButton(columnBtn, megaMenu);
        requestAnimationFrame(() => {
          const panel = megaMenu.querySelector('[data-child-submenu]:not(.hidden)');
          panel?.querySelector('a[href]')?.focus();
        });
        return;
      }

      if (e.key === 'ArrowLeft' && active.closest('[data-child-submenu]')) {
        const panel = active.closest('[data-child-submenu]');
        const handle = panel?.getAttribute('data-child-submenu');
        if (!handle) return;
        e.preventDefault();
        const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
        megaMenu.querySelector(`button[data-child-handle="${esc}"]`)?.focus();
        return;
      }

      if (e.key === 'ArrowUp' && idx === 0) {
        e.preventDefault();
        megaMenu.closest('li')?.querySelector('[data-menu-item] a')?.focus();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        let next = idx + delta;
        if (next < 0) next = list.length - 1;
        if (next >= list.length) next = 0;
        list[next]?.focus();
      }
    }

    isMegaMenuFocusableVisible(el, boundary) {
      let n = el;
      while (n && n !== boundary) {
        if (n.classList?.contains('hidden')) return false;
        if (n.getAttribute('aria-hidden') === 'true') return false;
        n = n.parentElement;
      }
      return true;
    }

    getMegaMenuFocusableList(dropdown) {
      const selector = 'a[href], button:not([disabled])';
      return [...dropdown.querySelectorAll(selector)].filter((node) => this.isMegaMenuFocusableVisible(node, dropdown));
    }

    getMegaMenuFirstFocusable(dropdown) {
      return this.getMegaMenuFocusableList(dropdown)[0];
    }

    showDesktopDropdown(dropdown, immediate = false) {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }

      if (this.submenuTimeout) {
        clearTimeout(this.submenuTimeout);
        this.submenuTimeout = null;
      }

      if (this.showTimeout) {
        clearTimeout(this.showTimeout);
        this.showTimeout = null;
      }

      const reveal = () => {
        this.hideAllDesktopDropdowns();
        dropdown.classList.remove('hidden');
        dropdown.classList.add('flex');
        dropdown.setAttribute('aria-hidden', 'false');
        this.activateFirstSubmenu(dropdown);
      };

      if (immediate) {
        reveal();
      } else {
        this.showTimeout = setTimeout(reveal, 150);
      }
    }

    hideDesktopDropdown(dropdown) {
      if (this.showTimeout) {
        clearTimeout(this.showTimeout);
        this.showTimeout = null;
      }

      this.hoverTimeout = setTimeout(() => {
        if (dropdown.contains(document.activeElement)) return;
        dropdown.classList.remove('flex');
        dropdown.classList.add('hidden');
        dropdown.setAttribute('aria-hidden', 'true');
      }, 175);
    }

    hideAllDesktopDropdowns() {
      const allDropdowns = this.querySelectorAll('[data-mega-menu]');
      const allLinks = this.querySelectorAll('[data-menu-item] a');

      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove('flex');
        dropdown.classList.add('hidden');
        dropdown.setAttribute('aria-hidden', 'true');
      });

      allLinks.forEach((link) => {
        link.classList.remove('nav-link-active');
      });
    }

    setupChildSubmenuHover() {
      const megaMenus = this.querySelectorAll('[data-mega-menu]');

      megaMenus.forEach((megaMenu) => {
        const childButtons = megaMenu.querySelectorAll('button[data-child-handle]');
        const childSubmenus = megaMenu.querySelectorAll('[data-child-submenu]');
        let hoverIntentTimer = null;

        const activate = (btn) => {
          this.setActiveSubmenuByButton(btn, megaMenu);
        };

        childButtons.forEach((childBtn) => {
          childBtn.addEventListener('mouseenter', () => {
            if (hoverIntentTimer) clearTimeout(hoverIntentTimer);
            hoverIntentTimer = setTimeout(() => activate(childBtn), 125);
          });

          childBtn.addEventListener('mouseleave', () => {
            if (hoverIntentTimer) {
              clearTimeout(hoverIntentTimer);
              hoverIntentTimer = null;
            }
          });

          childBtn.addEventListener('focusin', () => {
            if (hoverIntentTimer) {
              clearTimeout(hoverIntentTimer);
              hoverIntentTimer = null;
            }
            activate(childBtn);
          });

          childBtn.addEventListener('click', () => activate(childBtn));
        });

        megaMenu.addEventListener('mouseleave', () => {
          if (megaMenu.contains(document.activeElement)) return;

          if (hoverIntentTimer) {
            clearTimeout(hoverIntentTimer);
            hoverIntentTimer = null;
          }

          childSubmenus.forEach((submenu) => {
            submenu.classList.add('hidden');
            submenu.classList.remove('flex');
            submenu.setAttribute('aria-hidden', 'true');
          });
          childButtons.forEach((childBtn) => {
            childBtn.setAttribute('aria-expanded', 'false');
            childBtn.classList.remove('nav-link-active');
          });
        });
      });
    }

    activateFirstSubmenu(dropdown) {
      const childButtons = dropdown.querySelectorAll('button[data-child-handle]');
      const childSubmenus = dropdown.querySelectorAll('[data-child-submenu]');
      if (!childButtons.length) return;

      childSubmenus.forEach((panel) => {
        panel.classList.add('hidden');
        panel.classList.remove('flex');
        panel.setAttribute('aria-hidden', 'true');
      });
      childButtons.forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
        btn.classList.remove('nav-link-active');
      });

      const firstBtn = childButtons[0];
      const handle = firstBtn.dataset.childHandle;
      const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
      const firstPanel = dropdown.querySelector(`[data-child-submenu="${esc}"]`);
      if (firstPanel) {
        firstPanel.classList.remove('hidden');
        firstPanel.classList.add('flex');
        firstPanel.setAttribute('aria-hidden', 'false');
        firstBtn.setAttribute('aria-expanded', 'true');
        firstBtn.classList.add('nav-link-active');
      }
    }

    setActiveSubmenuByButton(btn, dropdown) {
      const button = btn?.closest?.('button[data-child-handle]') ?? (btn?.matches?.('button[data-child-handle]') ? btn : null);
      if (!button) return;

      const handle = button.dataset.childHandle;
      const childButtons = dropdown.querySelectorAll('button[data-child-handle]');
      const childSubmenus = dropdown.querySelectorAll('[data-child-submenu]');

      childSubmenus.forEach((panel) => {
        panel.classList.add('hidden');
        panel.classList.remove('flex');
        panel.setAttribute('aria-hidden', 'true');
      });
      childButtons.forEach((b) => {
        b.classList.remove('nav-link-active');
        b.setAttribute('aria-expanded', 'false');
      });

      const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
      const panel = dropdown.querySelector(`[data-child-submenu="${esc}"]`);
      if (panel) {
        button.classList.add('nav-link-active');

        panel.classList.remove('hidden');
        panel.classList.add('flex');
        panel.setAttribute('aria-hidden', 'false');
        button.setAttribute('aria-expanded', 'true');
      }
    }
  }

  customElements.define('scroll-header', Header);
}
